/**
 * Contextos de sesion soportados. Cada contexto tiene su propio token
 * (en TokenCacheService) y su propia ruta de login. Determinan tambien a
 * que pantalla redirigimos al usuario cuando su sesion expira.
 */
export type AuthContext =
  | 'owner'
  | 'superadmin'
  | 'pos'
  | 'pos-simple'
  | 'kitchen'
  | 'bar'
  | 'waiter'
  | 'delivery'
  | 'kiosk';

/**
 * Resuelve el contexto a partir de la URL actual del router.
 *
 * Convencion del routing: las pantallas operativas viven bajo
 * `/<slug>/<modulo>(/<sub>)`. El segmento del modulo (`mesero`, `cocina`,
 * `barra`, `pos`, `pos-simple`, `kiosco`) identifica el contexto. SuperAdmin
 * y Owner viven en el root (`/superadmin`, `/owner`).
 *
 * Si no se reconoce ningun segmento, asumimos `owner` (cualquier ruta del
 * dashboard administrativo del restaurante).
 */
export function detectAuthContext(url: string): AuthContext {
  const segments = url.split('/').filter(Boolean);
  const topLevel = segments[0]?.split('?')[0] ?? '';

  if (topLevel === 'owner') return 'owner';
  if (topLevel === 'superadmin') return 'superadmin';

  for (const segment of segments) {
    const clean = segment.split('?')[0];
    if (clean === 'pos-simple') return 'pos-simple';
    if (clean.startsWith('pos')) return 'pos';
    if (clean === 'cocina' || clean.startsWith('kitchen')) return 'kitchen';
    if (clean === 'barra') return 'bar';
    if (clean === 'mesero' || clean.startsWith('waiter')) return 'waiter';
    // delivery vive en /<slug>/delivery (igual que mesero/cocina/pos), NO en /delivery.
    // Antes se detectaba solo como topLevel '/delivery' (ruta inexistente) -> en
    // /<slug>/delivery caia al default 'owner' y al expirar limpiaba el token equivocado.
    if (clean === 'delivery') return 'delivery';
    if (clean === 'kiosco') return 'kiosk';
  }

  return 'owner';
}

/**
 * Extrae el slug del restaurante de la URL para rutas tenant-scoped
 * (`/<slug>/<modulo>`). Devuelve null para rutas globales (owner, superadmin)
 * que no llevan slug. delivery SÍ es tenant-scoped (`/<slug>/delivery`).
 */
export function extractRestaurantSlug(url: string, context: AuthContext): string | null {
  if (context === 'owner' || context === 'superadmin') {
    return null;
  }

  const segments = url.split('/').filter(Boolean);
  const first = segments[0]?.split('?')[0] ?? '';
  return first || null;
}

/**
 * Devuelve la ruta de login correspondiente al contexto. Para contextos
 * tenant-scoped el slug es obligatorio; sin slug devuelve null porque no
 * podemos construir la URL completa de forma segura.
 */
export function getLoginRouteForContext(context: AuthContext, slug: string | null): string | null {
  if (context === 'owner') return '/login';
  if (context === 'superadmin') return '/superadmin/login';
  if (!slug) return null;

  // delivery es tenant-scoped: su login ES /<slug>/delivery (el mismo link que llega por
  // WhatsApp). Antes apuntaba a /delivery/login, ruta inexistente -> 404 al expirar.
  const moduleByContext: Record<Exclude<AuthContext, 'owner' | 'superadmin'>, string> = {
    pos: 'pos',
    'pos-simple': 'pos-simple',
    kitchen: 'cocina',
    bar: 'barra',
    waiter: 'mesero',
    delivery: 'delivery',
    kiosk: 'kiosco'
  };

  const moduleSegment = moduleByContext[context];
  return `/${slug}/${moduleSegment}`;
}
